import React from 'react'
import { CardElement, useElements } from '@stripe/react-stripe-js'
import CardPaymentMethodFormViewModel from './CardPaymentMethodFormViewModel'
import { observer } from 'mobx-react'
import {
  Form,
  FormGroup,
  FormGroupContainer,
  FormRow,
  Help,
  TextInputContainer,
} from 'components/inputs'
import { CountryInput } from 'components/inputs/CountryInput'
import {
  StripeCardElementOptions,
  StripeElementChangeEvent,
} from '@stripe/stripe-js'
import Button from 'components/Button'
import cx from 'classnames'
import styles from './CardPaymentMethodForm.sass'

interface CardPaymentMethodFormProps {
  vm: CardPaymentMethodFormViewModel
}

export default observer(function CardPaymentMethodForm({
  vm,
}: CardPaymentMethodFormProps) {
  const elements = useElements()
  const handleSubmit = React.useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()
      vm.pay().then()
    },
    [elements, vm],
  )

  React.useEffect(() => {
    const card = elements?.getElement(CardElement)
    vm.card = card ?? null
  }, [])

  return (
    <Form onSubmit={handleSubmit}>
      <input type="submit" hidden disabled={vm.formDisabled} />
      <FormRow>
        <CardInput disabled={vm.formDisabled} vm={vm} />
      </FormRow>
      <FormRow>
        <TextInputContainer
          model={vm}
          field="fullName"
          label="Cardholder name"
          name="name"
          autoFocus
          placeholder="Please enter the full name as it is displayed on your card."
          disabled={vm.formDisabled}
          error={vm.validation.getError('fullName')}
          data-test="add-card-form-full-name-input"
        />
      </FormRow>
      <FormRow>
        <FormGroupContainer
          model={vm}
          field="country"
          htmlFor="country"
          label="Country"
          error={vm.validation.getError('country')}
        >
          <CountryInput
            disabled={vm.formDisabled}
            value={vm.country}
            onChange={vm.setCountry}
            data-test="add-card-form-country-input"
          />
        </FormGroupContainer>
      </FormRow>
      <FormRow>
        <TextInputContainer
          model={vm}
          field="zipCode"
          name="zip"
          label="Zip"
          placeholder="Zip Code"
          disabled={vm.formDisabled}
          error={vm.validation.getError('zipCode')}
          data-test="add-card-form-zip-input"
        />
      </FormRow>
      <FormRow></FormRow>
      <FormRow>
        <Button submit type="primary" size="wide">
          Pay
        </Button>
      </FormRow>
    </Form>
  )
})

interface CardInputProps {
  vm: CardPaymentMethodFormViewModel
  disabled: boolean
}

/**
 * The CardInput
 */
const CardInput = observer(function CardInput({
  vm,
  disabled,
}: CardInputProps) {
  const cardError = vm.validation.getError('card')
  const setCardError = React.useCallback(
    (error: string) => {
      if (!error) {
        vm.validation.clearErrors('card')
      } else {
        vm.validation.clearErrors('card')
        vm.validation.addErrors({ card: [error] })
      }
    },
    [vm],
  )

  const handleChange = React.useCallback(
    (event: StripeElementChangeEvent) => {
      setCardError(event.error?.message || '')
    },
    [setCardError],
  )
  return (
    <FormGroup
      label="Credit or debit card"
      data-test="add-card-form-card-input"
    >
      <CardElement
        onChange={handleChange}
        options={{
          ...CARD_ELEMENT_OPTIONS,
          disabled,
          classes: {
            ...CARD_ELEMENT_OPTIONS.classes,
            // Needed since we also populate errors ourselves.
            base: cx(
              CARD_ELEMENT_OPTIONS.classes?.base,
              cardError && styles.danger,
              disabled && styles.disabled,
            ),
          },
        }}
      />
      <Help error={cardError} />
    </FormGroup>
  )
})

const CARD_ELEMENT_OPTIONS: StripeCardElementOptions = {
  hidePostalCode: true,
  classes: {
    base: styles.control,
    invalid: styles.danger,
    focus: styles.focused,
  },
}
